import { Grid, Box, Spinner } from '@oresundsbron/bridge-ui';
import { FormStoreProvider } from '@oresundsbron/use-form';
import { FC, useCallback, useMemo } from 'react';
import SignUpForm from './Form';
import { ThankYou } from '../ThankYou';

import { pipe } from 'fp-ts/lib/function';
import {
  fromNullable,
  map,
  chain,
  alt,
  getOrElse,
  fromPredicate,
  of,
} from 'fp-ts/lib/Option';
import { SignupResponse } from '@oresundsbron/validators';
import { tapO } from '@oresundsbron/utilities';
import { useRouter } from 'next/router';
import { BackendQueryKeys, useTypeSafeQuery } from '@oresundsbron/api';
import { match } from 'fp-ts/lib/boolean';
import Error from '../Error';
import {
  SetPassword,
  useBannerVisibility,
  useSignUpStore,
} from '@oresundsbron/app-common';
import { SignupFormDataPersonalDetails } from '../brobizz';

const renderResult =
  (resp: SignupResponse | undefined, isPasswordSet: boolean) => () =>
    pipe(
      resp,
      fromNullable,
      chain(() =>
        pipe(
          isPasswordSet,
          fromPredicate((x) => !!x),
          map(() => <ThankYou ns="privateSignUp" />),
          alt(() => of(<SetPassword ns="privateSignUp" />))
        )
      )
    );

const renderForm = (
  token: string,
  data?: SignupFormDataPersonalDetails,
  business?: boolean
) =>
  pipe(
    data ? data : null,
    fromNullable,
    map((d) => <SignUpForm data={d} token={token} business={!!business} />),
    getOrElse(() => <Error />)
  );

export const SignUp: FC<{ business?: boolean }> = ({ business }) => {
  const { setBannerVisible } = useBannerVisibility();
  const { error, resp, isPasswordSet } = useSignUpStore();
  const { query } = useRouter();
  const isBankIDResult = query.complete === '1';

  const {
    data,
    isLoading,
    error: fetchingError,
  } = useTypeSafeQuery(
    BackendQueryKeys.BrobizzSignupInformation(query.token as string),
    ({ Signup }) => Signup.getSkyttelInformation(query.token as string),
    {
      enabled: !!query.token && query.token !== '',
    }
  );

  const pageError = useMemo(
    () => fetchingError || error,
    [fetchingError, error]
  );

  const renderContent = useCallback(
    (
      token: string,
      data?: SignupFormDataPersonalDetails,
      business?: boolean
    ) => {
      if (isBankIDResult) {
        setBannerVisible(false);
        return <ThankYou ns="privateSignUp" />;
      }

      return pipe(
        pageError,
        fromNullable,
        map(() => <Error />),
        alt(renderResult(resp, isPasswordSet)),
        tapO(() => setBannerVisible(false)),
        getOrElse(() => renderForm(token, data, business))
      );
    },
    [isBankIDResult, pageError, resp, isPasswordSet, setBannerVisible]
  );

  if (!query.token) {
    return null;
  }

  return (
    <FormStoreProvider>
      <Grid>
        {pipe(
          !isLoading,
          match(
            () => (
              <Box className="col-span-full justify-self-center text-5xl text-primary-600">
                <Spinner label="Loading..." />
              </Box>
            ),
            () =>
              renderContent(
                query.token as string,
                data as SignupFormDataPersonalDetails,
                business
              )
          )
        )}
      </Grid>
    </FormStoreProvider>
  );
};

export default SignUp;
